



import { CommonCardContainer } from "../../components/DashboardCards/cardStyles";
import {
  CommonGrid,
  CommonText,
  PageTitle,
} from "../../styledComponents/common";
import {
  ButtonIcon,
  ColoredButtonWithIcon,
} from "../../styledComponents/buttons";
import PlusWhite from "../../assets/img/icons/plus_white.svg";
import LineItemModal from "./LineItemModal";
import { useTranslation } from "react-i18next";
import { useState, useEffect, useRef, useMemo } from "react";
import LineItemsTable from "./LineItemsTable";

const LineItemSection = ({
  // create-mode props
  setLineItems,
  // shared
  currency,
  setIsLineItemModalOpen,
  // edit-mode props
  mode = "create", // "create" | "edit"
  pageType, // "detail" for read-only view
  lineItems = [],
  subtasks, // For detail view, can pass subtasks directly
  onAddLineItem,
  onUpdateLineItem,
  onDeleteLineItem,
  // detail view props
  onImageClick,
  onAudioClick,
  getDuration,
  pageName,
}) => {
  const isDetailView = pageType === "detail";
  const { t } = useTranslation();
  const [action, setAction] = useState('add');
  const [popupOpen, setPopupOpen] = useState(false);
  const [dataForEdit, setDataForEdit] = useState(null);
  const [itemArray, setItemArray] = useState([]);
  const [totalCalc, setTotalCalc] = useState("0.00");
  const prevSubtasksRef = useRef(null);
  const prevLineItemsRef = useRef(null);

  // Memoize the IDs string to prevent unnecessary re-renders
  const subtasksIdsString = useMemo(() => {
    if (isDetailView && Array.isArray(subtasks) && subtasks.length > 0) {
      return subtasks.map(s => s?.id).filter(Boolean).join(',');
    }
    return null;
  }, [subtasks, isDetailView]);

  const lineItemsIdsString = useMemo(() => {
    if (mode === "edit" && Array.isArray(lineItems) && lineItems.length > 0) {
      return lineItems.map(l => l?.id || l?.subtaskId).filter(Boolean).join(',');
    }
    return null;
  }, [lineItems, mode]);

  // Sync incoming items (edit mode or detail view) into local table
  useEffect(() => {
    if (isDetailView && subtasksIdsString) {
      const prevIds = prevSubtasksRef.current;
      
      if (subtasksIdsString !== prevIds) {
        prevSubtasksRef.current = subtasksIdsString;
        // For detail view, use subtasks directly
        const mapped = subtasks.map(item => ({
          id: item.id,
          itemType: item.item_type || "product",
          name: item.name || "",
          description: item.description || "",
          quantity:
            item.quantity !== undefined && item.quantity !== null
              ? item.quantity
              : 1,
          unitPrice: item.unit_price || "0.00",
          amount: item.amount || (item.quantity * (parseFloat(item.unit_price) || 0)),
          evidenceToggle: item.allow_evidences || false,
          evidences: item.evidences || [],
        }));
        setItemArray(mapped);
      }
    } else if (mode === "edit" && lineItemsIdsString) {
      const prevIds = prevLineItemsRef.current;
      
      if (lineItemsIdsString !== prevIds) {
        prevLineItemsRef.current = lineItemsIdsString;
        const mapped = lineItems.map(item => ({
          id: item.id || item.subtaskId,
          itemType: item.itemType || item.item_type || "product",
          name: item.name || "",
          description: item.description || "",
          quantity:
            item.quantity !== undefined && item.quantity !== null
              ? item.quantity
              : 1,
          unitPrice:
            item.unitPrice !== undefined && item.unitPrice !== null
              ? item.unitPrice
              : item.unit_price || "0.00",
          amount: item.amount || item.total || item.quantity * item.unit_price,
          evidenceToggle:
            item.evidenceToggle ??
            item.allow_evidences ??
            item.isAllowedEvidences ??
            false,
        }));
        setItemArray(mapped);
      }
    } else if ((isDetailView && !subtasksIdsString && prevSubtasksRef.current !== null) ||
               (mode === "edit" && !lineItemsIdsString && prevLineItemsRef.current !== null)) {
      // Clear array if data becomes unavailable
      if (isDetailView) {
        prevSubtasksRef.current = null;
      } else {
        prevLineItemsRef.current = null;
      }
      setItemArray([]);
    }
  }, [subtasksIdsString, lineItemsIdsString, mode, isDetailView, subtasks, lineItems]);

    useEffect(() => {
      totalCalculation();

      // Send item Array to Parent component (create mode only)
      if (mode === "create" && setLineItems) {
        const payload =
          itemArray?.length > 0
            ? itemArray.map((item, index) => ({
                item_type: item.itemType?.toLowerCase(),
                description: item.description,
                serial_number: index + 1,
                quantity: item.quantity,
                unit_price: item.unitPrice,
                allow_evidences: item.evidenceToggle,
                name: item.name,
              }))
            : [];
        setLineItems(payload);
      }
    }, [itemArray, mode, setLineItems]);
  
    const buildPayload = (item, indexOverride) => ({
      name: item.name,
      description: item.description,
      quantity: item.quantity,
      unit_price: item.unitPrice,
      allow_evidences: item.evidenceToggle,
      item_type: (item.itemType || "product").toLowerCase(),
      serial_number:
        indexOverride !== undefined ? indexOverride + 1 : item.serial_number,
    });

    const handleDeleteItem = index => {
      let newArray = [...itemArray];
      const [removed] = newArray.splice(index, 1);
      setItemArray(newArray);
      if (mode === "edit" && onDeleteLineItem && removed?.id) {
        onDeleteLineItem(removed);
      }
    };
  
    const totalCalculation = () => {
      if (itemArray && itemArray.length) {
        const total = itemArray.reduce(
          (sum, item) =>
            sum +
            (item.amount
              ? Number(item.amount)
              : Number(item.quantity) * Number(item.unitPrice)),
          0
        );
        setTotalCalc(total.toFixed(2));
      } else {
        setTotalCalc("0.00");
      }
    };
  
    const handleEditItem = index => {
      let item = itemArray[index];
      setDataForEdit({ ...item, index });
      setAction('edit');
      setPopupOpen(true);
    };
  
    const handleAddItems = items => {
      // Handle Edit Case
      if (items.action == 'edit') {
        let newArray = [...itemArray];
        newArray[items.index] = {
          id: newArray[items.index]?.id,
          itemType: items.itemType,
          name: items.name,
          description: items.description,
          quantity: items.quantity,
          unitPrice: items.unitPrice,
          amount: items.amount,
          evidenceToggle: items.evidenceToggle,
        };
        setItemArray(newArray);
        if (mode === "edit" && onUpdateLineItem) {
          const payload = buildPayload(newArray[items.index], items.index);
          onUpdateLineItem({ ...payload, id: newArray[items.index]?.id });
        }
        return;
      }

      // Handle Add Case
      let newItem = {
        id: items.id,
        itemType: items.itemType,
        name: items.name,
        description: items.description,
        quantity: items.quantity,
        unitPrice: items.unitPrice,
        amount: items.amount,
        evidenceToggle: items.evidenceToggle,
      };
      let newArray = [...itemArray, newItem];
      setItemArray(newArray);

      if (mode === "edit" && onAddLineItem) {
        const payload = buildPayload(newItem, newArray.length - 1);
        onAddLineItem(payload);
      }
    };
  
    const handleEvidenceToggleClick = index => {
      let newArr = [...itemArray];
      newArr[index].evidenceToggle = !newArr[index].evidenceToggle;
      setItemArray(newArr);
      if (mode === "edit" && onUpdateLineItem) {
        const payload = buildPayload(newArr[index], index);
        onUpdateLineItem({ ...payload, id: newArr[index]?.id });
      }
    };
  
    const handleAddItemClick = () => {
      setAction('add');
      setDataForEdit(null);
      setPopupOpen(true);
    };


  return (
    <CommonCardContainer  style={{ marginTop: "20px" }} bgColor="#EDF8FE">
      {!isDetailView && (
        <LineItemModal isOpen={popupOpen} toggle={() => setPopupOpen(false)} addItems={handleAddItems} action={action} dataForEdit={dataForEdit} currency={currency} />
      )}
      <CommonGrid margin="0 0 10px 0" alignItem="start" columns="1fr 200px">
        <PageTitle>Line Items</PageTitle>
        {!isDetailView && (
          <ColoredButtonWithIcon onClick={handleAddItemClick}>
            <ButtonIcon src={PlusWhite} alt="" />
            Add Line Items
          </ColoredButtonWithIcon>
        )}
      </CommonGrid>
      {/* table will be there */}
      {itemArray && itemArray.length > 0 ? (
        <LineItemsTable
          itemArray={itemArray}
          onEditItem={isDetailView ? undefined : handleEditItem}
          onDeleteItem={isDetailView ? undefined : handleDeleteItem}
          totalCalc={totalCalc}
          currency={currency}
          isDetailView={isDetailView}
          onImageClick={onImageClick}
          onAudioClick={onAudioClick}
          getDuration={getDuration}
          pageName={pageName}
        />
      ) : (
        <CommonText>No line items added yet</CommonText>
      )}
    </CommonCardContainer>
  );
};

export default LineItemSection;